import {
  Button,
  Calendar,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@portals-platform/ui';
import { CalendarIcon } from 'lucide-react';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { triggerClasses } from '../lib/control-classes.js';
import { useFieldA11y } from '../lib/field-context.js';
import { useFieldError } from '../lib/use-field-error.js';

export interface DateFieldProps {
  name: string;
  placeholder?: string;
  /** Intl options for the trigger label. Defaults to a medium date in the user's locale. */
  format?: Intl.DateTimeFormatOptions;
  locale?: string;
  disabled?: boolean;
  fromDate?: Date;
  toDate?: Date;
  className?: string;
}

const DEFAULT_FORMAT: Intl.DateTimeFormatOptions = { dateStyle: 'medium' };

/**
 * Single date. The form value is a `Date`; render it with the caller's locale rather than
 * `toLocaleDateString()` defaults so a brand can pin a format.
 */
export function DateField({
  name,
  placeholder = 'Select a date',
  format = DEFAULT_FORMAT,
  locale,
  disabled,
  fromDate,
  toDate,
  className,
}: DateFieldProps) {
  const { control } = useFormContext();
  const [open, setOpen] = useState(false);
  const hasError = !!useFieldError(name);
  const a11y = useFieldA11y(name);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const value = field.value instanceof Date ? field.value : undefined;

        return (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                {...a11y}
                type="button"
                variant="outline"
                disabled={disabled}
                onBlur={field.onBlur}
                className={triggerClasses({ hasError, className })}
              >
                <span className={cn(!value && 'text-muted-foreground/60')}>
                  {value ? new Intl.DateTimeFormat(locale, format).format(value) : placeholder}
                </span>
                <CalendarIcon
                  className="size-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                autoFocus
                selected={value}
                onSelect={(next: Date | undefined) => {
                  field.onChange(next);
                  // Close on pick — leaving it open makes the control feel unresponsive,
                  // and there is nothing else to choose.
                  if (next) setOpen(false);
                }}
                {...(fromDate || toDate
                  ? {
                      disabled: [
                        ...(fromDate ? [{ before: fromDate }] : []),
                        ...(toDate ? [{ after: toDate }] : []),
                      ],
                    }
                  : {})}
              />
            </PopoverContent>
          </Popover>
        );
      }}
    />
  );
}
